// Iter 83.5 / Phase E4 Sprint 5 — Loan Sanction Letter print.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LoanSanctionPrint() {
    const { loanId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/print/loan-sanction/${loanId}`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load sanction letter"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [loanId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="loan-sanction-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;
    const { loan, employee, policy, schedule, company, generated_at } = data;

    return (
        <div className="bg-white min-h-screen p-12 font-serif" data-testid="loan-sanction-print">
            <style>{`@media print { @page { size: A4; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            <header className="border-b-4 border-slate-900 pb-4 mb-6 flex justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name}</div>
                    <h1 className="text-3xl font-bold mt-1">Loan Sanction Letter</h1>
                    <div className="text-sm text-slate-600 mt-1">Iter 83.5 / Sprint 5 · Employee Salary Loan</div>
                </div>
                <div className="text-right text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Issued</div>
                    <div className="font-mono">{(generated_at || "").slice(0, 10)}</div>
                </div>
            </header>

            <p className="text-sm mb-4">To,<br />
                <strong>{employee?.full_name}</strong> ({employee?.employee_code})<br />
                {employee?.designation} · {employee?.department}<br />
                {employee?.work_email || employee?.personal_email}
            </p>
            <p className="text-sm mb-4">Dear {employee?.full_name?.split(" ")[0] || "Colleague"},</p>
            <p className="text-sm mb-4 leading-relaxed">
                We are pleased to inform you that your application for an employee loan dated{" "}
                <strong>{(loan.applied_at || "").slice(0, 10)}</strong> has been <strong>approved</strong>.
                The terms and conditions of this loan are set out below.
            </p>

            <section className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm border-y border-slate-300 py-4 mb-4">
                <Pair label="Loan ID" value={loan.id} mono />
                <Pair label="Policy" value={loan.policy_code_snapshot} />
                <Pair label="Principal" value={fmtMoney(loan.principal)} />
                <Pair label="Interest p.a." value={`${loan.annual_rate_pct}%`} />
                <Pair label="Tenure" value={`${loan.tenure_months} months`} />
                <Pair label="Monthly EMI" value={fmtMoney(loan.emi)} />
                <Pair label="Total Interest" value={fmtMoney(loan.total_interest)} />
                <Pair label="Total Repayment" value={fmtMoney(loan.total_repayment)} />
                <Pair label="Foreclosure Mode" value={loan.policy_foreclose_mode_snapshot} />
                <Pair label="Loan Status" value={loan.status} />
                {loan.disbursement && (
                    <>
                        <Pair label="Disbursed On" value={loan.disbursement.disbursed_on} />
                        <Pair label="Disbursement Ref." value={loan.disbursement.reference_number} />
                    </>
                )}
            </section>

            <section className="text-sm mb-4 leading-relaxed">
                <h2 className="font-bold mb-2">Key Terms</h2>
                <ol className="list-decimal pl-5 space-y-1 text-xs">
                    <li>The EMI of {fmtMoney(loan.emi)} will be auto-recovered from your monthly salary, starting the next payroll cycle after disbursement.</li>
                    <li>You may foreclose at any time. Foreclosure mode for this policy is <strong>{loan.policy_foreclose_mode_snapshot}</strong>.</li>
                    <li>On separation, the outstanding principal becomes immediately payable. {policy?.allow_negative_fnf ? "Per policy, any shortfall above your FNF will be raised as an Outstanding Recovery." : "Per policy, the loan must be cleared in your FNF or via direct repayment."}</li>
                    <li>All amounts are net of applicable TDS where relevant. The repayment schedule attached below is generated using the deterministic loan engine and is binding.</li>
                </ol>
            </section>

            {schedule && schedule.length > 0 && (
                <section className="mb-6">
                    <h2 className="text-sm font-bold mb-2">Repayment Schedule</h2>
                    <table className="w-full text-[11px]">
                        <thead className="bg-slate-100">
                            <tr>
                                <th className="text-left px-2 py-1">#</th>
                                <th className="text-left px-2 py-1">Due Date</th>
                                <th className="text-right px-2 py-1">EMI</th>
                                <th className="text-right px-2 py-1">Principal</th>
                                <th className="text-right px-2 py-1">Interest</th>
                                <th className="text-right px-2 py-1">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedule.slice(0, 36).map((s, i) => (
                                <tr key={i} className="border-b">
                                    <td className="px-2 py-0.5">{s.instalment_no || (i + 1)}</td>
                                    <td className="px-2 py-0.5">{s.due_date}</td>
                                    <td className="px-2 py-0.5 text-right font-mono">{fmtMoney(s.emi)}</td>
                                    <td className="px-2 py-0.5 text-right font-mono">{fmtMoney(s.principal_component)}</td>
                                    <td className="px-2 py-0.5 text-right font-mono">{fmtMoney(s.interest_component)}</td>
                                    <td className="px-2 py-0.5 text-right font-mono">{fmtMoney(s.closing_balance)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <section className="mt-8 grid grid-cols-2 gap-12">
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">Employee Signature · Date</div>
                    <div className="text-[10px] text-slate-500 mt-1">{employee?.full_name}</div>
                </div>
                <div>
                    <div className="border-t border-slate-700 pt-1 text-xs">Authorised Signatory</div>
                    <div className="text-[10px] text-slate-500 mt-1">For {company.legal_name}</div>
                    {loan.approval && (
                        <div className="text-[10px] text-slate-500 mt-1">
                            Approved by {loan.approval.approver_email} on {(loan.approval.approved_at || "").slice(0, 10)}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function Pair({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={mono ? "font-mono" : ""}>{value || "—"}</div>
        </div>
    );
}
